<template>
  <div class="picker">
    <div v-if="label">
      <label class="fw-bold">{{ label }}</label>
    </div>
    <date-picker
      is-required
      v-model="internalDate"
      :select-attribute="attribute"
      @update:modelValue="newDate"
    >
      <template
        v-if="withInput"
        v-slot="{ inputEvents }"
      >
        <custom-input
          class="input"
          :model-value="formattedDate"
          v-on="inputEvents"
        />
      </template>
    </date-picker>
  </div>
</template>

<script>
import { ref, watch, computed } from 'vue';

import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { DatePicker } from 'v-calendar';

import colors from '../../assets/css/colors.scss';
import CustomInput from '../common/CustomInput.vue';

dayjs.extend(customParseFormat);

export default {
  name: 'Picker',
  components: { CustomInput, DatePicker },
  emits: ['update:modelValue'],
  props: {
    withInput: {
      default: false,
      type: Boolean,
    },
    label: {
      default: '',
      type: String,
    },
    modelValue: String,
  },

  setup(props, { emit }) {
    // Convert incoming DD-MM-YYYY string to Date object for v-calendar
    const parseDate = (dateStr) => {
      if (!dateStr) return new Date();
      const parsed = dayjs(dateStr, 'DD-MM-YYYY');
      return parsed.isValid() ? parsed.toDate() : new Date();
    };

    const internalDate = ref(parseDate(props.modelValue));

    // Formatted date for display in input
    const formattedDate = computed(() => dayjs(internalDate.value).format('DD-MM-YYYY'));

    watch(() => props.modelValue, (newVal) => {
      internalDate.value = parseDate(newVal);
    });

    function newDate(dateValue) {
      // v-calendar 3.x returns Date object, convert to DD-MM-YYYY string
      const formatted = dayjs(dateValue).format('DD-MM-YYYY');
      emit('update:modelValue', formatted);
    }

    const attribute = {
      highlight: {
        start: {
          style: {
            backgroundColor: colors.blue,
            fillMode: 'outline',
          },
          contentStyle: {
            color: 'white',
          },
        },
      },
    };
    return {
      attribute,
      colors,
      formattedDate,
      internalDate,
      newDate,
    };
  },
};
</script>

<style lang="scss" scoped>
@import '../../assets/css/colors.scss';
@import '../../../node_modules/v-calendar/dist/style.css';

::v-deep(.vc-day-content) {
  &:hover, &:focus {
    background-color: $blue;
    color: white;
  }
}

::v-deep(.vc-focusable) {
  &:hover,
  {
    background-color: $blue;
    color: white;
  }
}

::v-deep(.vc-container) {
  border-color: $light-grey;
}

label {
  margin-right: 15px;
  margin-bottom: 20px;
  min-width: 45px;
}

.picker {
  display: flex;
  align-items: center;
}

.input {
  width: 100px;
}
</style>
